/**
 * A component that renders a sub-chapter heading in a PDF document
 * @param {string} heading - The text content of the heading
 * @param {string} [id] - Optional ID attribute for the heading element
 * @param {number} [minPresenceAhead] - Minimum space required ahead of the heading (in points)
 * @param {boolean} [withBreak] - Whether to force a page break before the heading
 * @param {object} [headingStyles] - Optional custom styles to apply to the heading
 * @returns {JSX.Element} A React component that displays a formatted sub-chapter heading
 * @see {@link https://react-pdf.org/ | React-PDF}
 */

import { StyleSheet, Text, View } from '@react-pdf/renderer';

interface ISubChapterHeadingProps {
  heading: string;
  id?: string;
  hide?: boolean;
  minPresenceAhead?: number;
  withBreak?: boolean;
  headingStyles?: { [key: string]: unknown };
}

export const SubChapterHeading = ({
  heading,
  id = '',
  minPresenceAhead = 100,
  withBreak,
  headingStyles = {},
}: ISubChapterHeadingProps) => (
  <>
    <View minPresenceAhead={minPresenceAhead} />
    <Text id={id} wrap={false} style={{ ...styles.heading, ...headingStyles }} break={withBreak}>
      {heading}
    </Text>
  </>
);

const styles = StyleSheet.create({
  heading: {
    fontFamily: 'Noto',
    fontSize: '18px',
    lineHeight: 1.33,
    marginBottom: 28,
    color: '#212121',
    fontWeight: 300,
  },
});
